import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useChat } from "@/contexts/ChatContext";
import { ChatCircleDots, ArrowSquareOut, X as XIcon, Minus, ArrowsOut } from "@phosphor-icons/react";
import {
  ConversationsList, ChatWindow, NewConversationModal, conversationTitle,
} from "@/components/chat/ChatComponents";

/**
 * Botón con badge en la barra superior. Al pulsar abre un panel deslizable
 * desde la derecha con la lista de conversaciones y la ventana de chat
 * activa, sin salir de la página actual. Incluye un botón para abrir el
 * chat en pantalla completa (`/chat`).
 */
export default function ChatDrawer() {
  const { user } = useAuth();
  const { totalUnread, conversations, refreshConversations } = useChat();
  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [active, setActive] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const navigate = useNavigate();

  // Cuando alguien (p.ej. el botón "carpeta" en el header del chat) emite
  // este evento, minimizamos el drawer en vez de cerrarlo, para que el
  // usuario pueda seguir viendo el chat mientras navega por el expediente.
  useEffect(() => {
    const onMinimize = () => {
      if (open) setMinimized(true);
    };
    window.addEventListener("chat:minimize", onMinimize);
    return () => window.removeEventListener("chat:minimize", onMinimize);
  }, [open]);

  if (!user) return null;

  const handleOpen = () => {
    setOpen(true);
    setMinimized(false);
    if (!active && conversations.length > 0) {
      const general = conversations.find((c) => c.type === "general") || conversations[0];
      setActive(general);
    }
  };

  const onCreated = async (conv) => {
    await refreshConversations();
    setActive(conv);
  };

  // --- Burbuja minimizada (esquina inferior derecha) ---------------------
  const Minimized = () => {
    const label = active ? conversationTitle(active, user.id) : "Chat";
    return (
      <button
        onClick={() => setMinimized(false)}
        data-testid="chat-drawer-restore"
        title={active ? `Continuar: ${label}` : "Volver al chat"}
        className="fixed bottom-5 right-5 z-40 inline-flex items-center gap-2 bg-white border border-border shadow-lg rounded-full px-3.5 h-11 hover:shadow-xl hover:bg-orange-50 transition"
      >
        <ChatCircleDots className="w-5 h-5 text-brand" weight="fill" />
        <span className="text-sm font-semibold text-foreground max-w-[220px] truncate">
          {label}
        </span>
        {totalUnread > 0 && (
          <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-status-error-fg text-white text-[11px] font-bold flex items-center justify-center">
            {totalUnread > 99 ? "99+" : totalUnread}
          </span>
        )}
        <ArrowsOut className="w-3.5 h-3.5 text-muted ml-1" weight="bold" />
      </button>
    );
  };

  return (
    <>
      <button
        onClick={handleOpen}
        data-testid="chat-drawer-trigger"
        title="Chat interno"
        className="relative p-2 rounded-md text-sidebar-fg-muted hover:text-sidebar-fg hover:bg-sidebar-2 transition-colors"
      >
        <ChatCircleDots className="w-5 h-5" weight="bold" />
        {totalUnread > 0 && (
          <span
            data-testid="chat-drawer-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-status-error-fg text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-sidebar-1"
          >
            {totalUnread > 99 ? "99+" : totalUnread}
          </span>
        )}
      </button>

      {open && minimized && <Minimized />}
      {open && !minimized && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/50"
            onClick={() => setMinimized(true)}
            data-testid="chat-drawer-overlay"
          />
          <aside className="fixed top-0 right-0 z-50 h-full w-full sm:w-[720px] bg-white flex flex-col shadow-2xl animate-in slide-in-from-right" data-testid="chat-drawer-panel">
            <div className="h-12 px-3 border-b border-border flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2 font-semibold text-sm">
                <ChatCircleDots className="w-4 h-4 text-brand" weight="fill" /> Chat
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setMinimized(true)}
                  data-testid="chat-drawer-minimize"
                  title="Minimizar (sigue activo en esquina inferior)"
                  className="p-2 rounded hover:bg-muted/40 text-muted-foreground"
                >
                  <Minus className="w-4 h-4" weight="bold" />
                </button>
                <button
                  onClick={() => { setOpen(false); navigate("/chat"); }}
                  data-testid="chat-drawer-fullscreen"
                  title="Pantalla completa"
                  className="p-2 rounded hover:bg-muted/40 text-muted-foreground"
                >
                  <ArrowSquareOut className="w-4 h-4" weight="bold" />
                </button>
                <button
                  onClick={() => { setOpen(false); setMinimized(false); }}
                  data-testid="chat-drawer-close"
                  className="p-2 rounded hover:bg-muted/40 text-muted-foreground"
                >
                  <XIcon className="w-4 h-4" weight="bold" />
                </button>
              </div>
            </div>
            <div className="flex-1 flex min-h-0">
              <div className="w-72 border-r border-border flex flex-col min-h-0 hidden sm:flex bg-white">
                <ConversationsList
                  conversations={conversations}
                  activeId={active?.id}
                  onSelect={setActive}
                  onNew={() => setNewOpen(true)}
                  currentUserId={user.id}
                />
              </div>
              <ChatWindow
                conversation={active}
                onConversationChanged={(c) => setActive(c)}
                key={active?.id || "empty"}
              />
            </div>
          </aside>
          <NewConversationModal
            open={newOpen}
            onOpenChange={setNewOpen}
            onCreated={onCreated}
          />
        </>
      )}
    </>
  );
}
